// Facturation à venir : planning de facturation Flex des chantiers en cours, plus les commandes GG/GA de N-1
// qui se facturent cette année (hypothèses de la maquette : 90 % facturés, 65 % d'achats, 8 % de MO).

import { ymAdd } from './compute.js';
import type { OpsChantier, OpsN1Order } from './types.js';

export const OPS_N1 = { f: 0.9, a: 0.65, mo: 0.08 } as const;

/** Origine d'un montant à facturer : planning Flex, chantier en dérive, démarrage à venir, commande N-1. */
export type BillingKind = 'b' | 'r' | 's' | 'n';

export interface BillingItem {
  code: string | null;
  label: string;
  v: number;
  k: BillingKind;
  /** Pas de ventilation Flex : montant estimé. */
  estimate?: boolean;
}

export interface BillingMonth {
  m: string;
  b: number;
  r: number;
  s: number;
  n: number;
  total: number;
  items: BillingItem[];
}

export interface BillingSummary {
  months: BillingMonth[];
  /** Reste à facturer des chantiers, dont celui des chantiers en dérive et celui sans date de facturation. */
  raf: number;
  rafR: number;
  rafSansDate: number;
  /** Facturation prévue d'ici la fin de l'année. */
  fy: number;
  /** Commandes N-1 retenues : montant commandé, facturation attendue, coûts attendus. */
  n1: { n: number; v: number; f: number; c: number };
}

/** `count` mois à partir du mois de la date d'arrêté. */
export function billingAhead(
  rows: OpsChantier[],
  n1: OpsN1Order[],
  asOf: string,
  count = 12,
): BillingSummary {
  const m0 = asOf.slice(0, 7);
  const months: BillingMonth[] = Array.from({ length: count }, (_, i) => ({
    m: ymAdd(m0, i),
    b: 0,
    r: 0,
    s: 0,
    n: 0,
    total: 0,
    items: [],
  }));
  const at = new Map(months.map((m) => [m.m, m]));
  for (const r of rows) {
    const k: BillingKind = r.deb && r.deb > asOf ? 's' : r.c.e === 'r' ? 'r' : 'b';
    for (const p of r.P.months) {
      const t = at.get(p.m);
      if (!t || p.f <= 0) continue;
      t[k] += p.f;
      t.items.push({ code: r.code, label: r.nom || r.cli || r.code, v: p.f, k, estimate: r.P.T <= 0 });
    }
  }
  const n1Sum = { n: n1.length, v: 0, f: 0, c: 0 };
  for (const x of n1) {
    n1Sum.v += x.v;
    n1Sum.f += x.v * OPS_N1.f;
    n1Sum.c += x.v * (OPS_N1.a + OPS_N1.mo);
    const t = at.get(x.m);
    if (!t) continue;
    const f = x.v * OPS_N1.f;
    t.n += f;
    t.items.push({ code: x.code, label: x.ds || x.cli || x.code || 'Commande N-1', v: f, k: 'n' });
  }
  for (const t of months) {
    t.total = t.b + t.r + t.s + t.n;
    t.items.sort((a, b) => b.v - a.v);
  }
  const year = asOf.slice(0, 4);
  return {
    months,
    raf: rows.reduce((a, r) => a + r.raf, 0),
    rafR: rows.filter((r) => r.c.e === 'r').reduce((a, r) => a + r.raf, 0),
    rafSansDate: rows.reduce((a, r) => a + (r.P.sd.f || 0), 0),
    fy: months.filter((t) => t.m.slice(0, 4) === year).reduce((a, t) => a + t.total, 0),
    n1: n1Sum,
  };
}
